import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AddActivity, AddEvent, AddTeam, Events } from '../models/events-details.model';
import { AddScore, EventScores } from '../models/event-scores.model';
import { LoginRequest, LoginSsoRequest } from '../models/external/login-request.model';
import { LoginResponse } from '../models/external/login-response.model';
import { MemberDetails } from '../models/member-details.model';
import { MemberProfile } from '../models/member-profile.model';
import { MembersList } from '../models/members-list.model';
import { StaticData } from '../models/static-data.model';
import { SummaryResponse } from '../models/summary.model';
import {
    AppliedTimeOff,
    ApplyTimeOffRequest,
    ApprovalResponse,
    TimeOffAudit,
    TimeOffSummary
} from '../models/time-off.model';
import { CompOffListResponse, CompOffRecord } from '../models/comp-off.model';
import { StaticSessionStorageKey, TokenSessionStorageKey, UserSessionStorageKey } from './auth.service';
import { environment } from '../../../environments/environment';

import { catchError, concatMap, map, Observable, of, throwError } from 'rxjs';
import { MemberAttendanceBody, MissingAttendanceResponse, MissingAttendanceSummaryResponse } from '../models/attendance.model';

@Injectable({ providedIn: 'root' })
export class BackendService {
    InterceptorSkipHeader = new HttpHeaders({
        'X-Skip-Interceptor': ''
    });

    constructor(
        private readonly http: HttpClient,
        private readonly snackBar: MatSnackBar
    ) { }

    login(loginData: LoginRequest): Observable<LoginResponse> {
        const url = this.createUrl(environment.LOGIN_ENDPOINT);

        return this.http
            .post<LoginResponse>(
                url,
                loginData,
                { headers: this.InterceptorSkipHeader })
            .pipe(
                concatMap((response: LoginResponse) => {
                    sessionStorage.setItem(TokenSessionStorageKey, response.idtoken);
                    sessionStorage.setItem(UserSessionStorageKey, JSON.stringify(response.user));

                    let staticUrl = this.createUrl(environment.STATIC);

                    return this.http.get<StaticData>(
                        staticUrl
                    )
                        .pipe(
                            map((staticResponse: any) => {
                                sessionStorage.setItem(StaticSessionStorageKey, JSON.stringify(staticResponse.body));
                                return response;
                            })
                        );
                }),
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    loginUsingSso(loginData: LoginSsoRequest): Observable<any> {
        const url = this.createUrl(environment.LOGIN_SSO_ENDPOINT);

        return this.http
            .post<any>(
                url,
                loginData,
                { headers: this.InterceptorSkipHeader })
            .pipe(
                concatMap((response) => this.storeSessionAndFetchStatic(response.idtoken, response.user)),
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Stores the token + user, then fetches and stores static data. Shared by the SSO login flow
     * and the SSO-portal fragment hydrator (which already has idtoken + user, so it skips
     * MSAL/ssologin). The token is set in sessionStorage first so the TokenInterceptor attaches it
     * to the /static request.
     */
    storeSessionAndFetchStatic(idtoken: string, user: any): Observable<any> {
        sessionStorage.setItem(TokenSessionStorageKey, idtoken);
        sessionStorage.setItem(UserSessionStorageKey, JSON.stringify(user));

        const staticUrl = this.createUrl(environment.STATIC);

        return this.http.get<StaticData>(staticUrl).pipe(
            map((staticResponse: any) => {
                sessionStorage.setItem(StaticSessionStorageKey, JSON.stringify(staticResponse.body));
                return { idtoken, user };
            })
        );
    }

    /**
     * Re-fetches /static and refreshes the cached copy in sessionStorage. Called on app
     * bootstrap (see AppComponent) so a page refresh picks up Config Portal changes — new
     * teams, locations, shift timings, permissions — without requiring the user to re-login.
     *
     * A cache-busting `_t` param bypasses the 1-hour browser HTTP cache on /static
     * (Cache-Control: private, max-age=3600). Failures are swallowed so a transient error
     * never blocks startup or wipes the already-cached static data.
     */
    refreshStaticData(): Observable<StaticData | null> {
        const staticUrl = this.createUrl(environment.STATIC);
        const params = new HttpParams().set('_t', Date.now().toString());

        return this.http.get<StaticData>(staticUrl, { params }).pipe(
            map((staticResponse: any) => {
                sessionStorage.setItem(StaticSessionStorageKey, JSON.stringify(staticResponse.body));
                return staticResponse.body as StaticData;
            }),
            catchError((error: HttpErrorResponse) => {
                console.error('Failed to refresh static data on bootstrap:', error);
                return of(null);
            })
        );
    }

    getOrganizationSummary(): Observable<SummaryResponse> {
        const url = this.createUrl(environment.EMPLOYEES.ORGANIZATION_SUMMARY_ENDPOINT);
        const params = new HttpParams().set('_t', Date.now().toString());

        return this.http.get<SummaryResponse>(url, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    getOrganizationMembers(): Observable<MembersList> {
        const url = this.createUrl(environment.EMPLOYEES.MEMBER_LIST_ENDPOINT);
        const params = new HttpParams().set('_t', Date.now().toString());

        return this.http.get<MembersList>(url, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: MembersList | { body: MembersList }): MembersList => {
                    return (response as { body: MembersList }).body ?? (response as MembersList);
                })
            );
    }

    addMember(member: any): Observable<Object> {
        const url = this.createUrl(environment.EMPLOYEES.ADD_MEMBER_ENDPOINT);

        return this.http.post(
            url,
            member
        )
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    updateMember(email: string, member: any): Observable<Object> {
        const url = this.createUrl(environment.EMPLOYEES.UPDATE_MEMBER_ENDPOINT, email);

        return this.http.put(
            url,
            member
        )
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    deleteMember(email: string): Observable<Object> {
        const url = this.createUrl(environment.EMPLOYEES.DELETE_MEMBER_ENDPOINT, email);

        return this.http.delete(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    getUserDetails(email: string): Observable<any> {
        const url = this.createUrl(environment.USER_DETAILS, email);

        return this.http.get<MemberDetails>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getUserProfile(email: string): Observable<any> {
        const url = this.createUrl(environment.USER_PROFILE, email);

        return this.http.get<MemberProfile>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getTimeOffOverview(email: string): Observable<any> {
        const url = this.createUrl(environment.TIME_OFF.SUMMARY, email);

        return this.http.get<TimeOffSummary>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getTimeOffAudit(email: string): Observable<any> {
        const url = this.createUrl(environment.TIME_OFF.AUDIT, email);

        return this.http.get<TimeOffAudit[]>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getTimeOffList(email: string): Observable<any> {
        const url = this.createUrl(environment.TIME_OFF.MEMBER_PENDING_APPROVAL, email);

        return this.http.get<ApprovalResponse>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    applyTimeOff(email: string, body: ApplyTimeOffRequest): Observable<any> {
        const url = this.createUrl(environment.TIME_OFF.APPLY_TIME_OFF, email);

        return this.http.post<string>(
            url,
            body
        )
            .pipe(
                map((response: any) => response.message)
            );
    }

    cancelTimeOff(timeOffId: string, email: string): Observable<any> {
        let url = this.createUrl(environment.TIME_OFF.CANCEL, email);
        url = url.replace('{timeOffId}', timeOffId);

        return this.http.delete<string>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.message)
            );
    }

    getTimeOffHistory(email: string): Observable<any> {
        const url = this.createUrl(environment.TIME_OFF.HISTORY, email);

        return this.http.get<AppliedTimeOff[]>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    updateAttendance(year: string, month: string, updatedRecord: any): Observable<any> {
        // PUT /wgteam/attendance/{year}/{month}
        let url = this.createUrl(environment.ATTENDANCE.MONTHLY);
        url = url.replace('{year}', year).replace('{month}', month);

        return this.http.put<any>(url, updatedRecord)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    uploadAttendance(uploadContent: any): Observable<Object> {
        const url = this.createUrl(environment.ATTENDANCE.UPLOAD)
        return this.http.post(
            url,
            uploadContent
        )
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    getMonthAttendance(year: string, month: string, memberId?: string): Observable<any> {
        let url = this.createUrl(environment.ATTENDANCE.MONTHLY);
        url = url.replace('{year}', year).replace('{month}', month);

        let params = new HttpParams();
        if (memberId) {
            params = params.set('member_id', memberId);
        }

        return this.http.get(url, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getNextRecords(year: string, month: string, snode: string | null, stype: string | null, memberId?: string): Observable<any> {
        let url = this.createUrl(environment.ATTENDANCE.MONTHLY);
        url = url.replace('{year}', year).replace('{month}', month);

        let params = new HttpParams();
        if (snode) { params = params.set('snode', snode); }
        if (stype) { params = params.set('stype', stype); }
        if (memberId) { params = params.set('member_id', memberId); }

        return this.http.get(url, { params: params })
          .pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            map((response: any) => response.body)
          );
      }

    getMissingAttendance(year: string, memberId: string, month?: string): Observable<MissingAttendanceResponse> {
        const url = this.createUrl(environment.ATTENDANCE.MISSING);

        let params = new HttpParams()
            .set('year', year)
            .set('member_id', memberId);
        if (month) {
            params = params.set('month', month);
        }

        return this.http.get<MissingAttendanceResponse>(url, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body as MissingAttendanceResponse)
            );
    }

    getMissingAttendanceSummary(year: string): Observable<MissingAttendanceSummaryResponse> {
        const url = this.createUrl(environment.ATTENDANCE.MISSING_SUMMARY);
        const params = new HttpParams().set('year', year);

        return this.http.get<MissingAttendanceSummaryResponse>(url, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body as MissingAttendanceSummaryResponse)
            );
    }

    getCompOffRecords(year: string, month: string, status?: string, memberId?: string): Observable<CompOffListResponse> {
        const url = this.createUrl(environment.COMPOFF.LIST);
        let params = new HttpParams().set('year', year).set('month', month);
        if (status) {
            params = params.set('status', status);
        }
        if (memberId) {
            params = params.set('member_id', memberId);
        }

        return this.http.get<CompOffListResponse>(url, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body as CompOffListResponse)
            );
    }

    addCompOff(record: Partial<CompOffRecord>): Observable<any> {
        const url = this.createUrl(environment.COMPOFF.ADD);

        return this.http.post<any>(url, record)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    updateCompOff(compoffId: string, record: Partial<CompOffRecord>): Observable<any> {
        let url = this.createUrl(environment.COMPOFF.UPDATE);
        url = url.replace('{compoffId}', compoffId);

        return this.http.put<any>(url, record)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    deleteCompOff(compoffId: string): Observable<any> {
        let url = this.createUrl(environment.COMPOFF.DELETE);
        url = url.replace('{compoffId}', compoffId);

        return this.http.delete<any>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    approveCompOff(compoffId: string, status: string, comments?: string): Observable<any> {
        let url = this.createUrl(environment.COMPOFF.APPROVE);
        url = url.replace('{compoffId}', compoffId);

        const body = {
            COMPOFF_STATUS: status,
            COMMENTS: comments || ''
        };

        return this.http.put<any>(url, body)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    batchApproveCompOff(memberId: string, year: number, month: string): Observable<any> {
        const url = this.createUrl(environment.COMPOFF.BATCH_APPROVE);
        const body = {
            MEMBER_ID: memberId,
            YEAR: year,
            MONTH: month
        };

        return this.http.put<any>(url, body)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    markCompoffUploaded(memberId: string, year: number, month: string): Observable<any> {
        const url = this.createUrl(environment.COMPOFF.UPLOAD);
        const body = {
            MEMBER_ID: memberId,
            YEAR: year,
            MONTH: month
        };

        return this.http.put<any>(url, body)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    // getMemberAttendance removed: backend endpoint no longer available

    private createUrl(url: string, email = ''): string {
        return environment.BASE_URL + url.replace('{email}', email);
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        if (error.status == 400) {
            const errorBody = error.error as unknown;
            const message = (errorBody && typeof errorBody === 'object' && 'message' in errorBody)
                ? (errorBody as { message?: string }).message ?? 'Bad request. Please check your input.'
                : 'Bad request. Please check your input.';
            this.snackBar.open(message, '✖', {
                duration: 5000,
                panelClass: ['red-snackbar'],
            });
        }
        else if (error.status == 401 || error.status == 403) {
            this.snackBar.open('Unauthorized. You are not allowed for this action.', '✖', {
                duration: 5000,
                panelClass: ['red-snackbar'],
            });
        }
        else {
            this.snackBar.open('Something went wrong. Please contact the admin.', '✖', {
                duration: 5000,
                panelClass: ['red-snackbar'],
            });
        }

        return throwError(() => error);
    }

    getPendingTimeOffApprovalsList(email: string): Observable<any> {
        const url = this.createUrl(environment.APPROVALS.MANAGER_PENDING_APPROVAL, email);

        return this.http.get<ApprovalResponse>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getPendingTimeOffApprovalsManagerList(): Observable<any> {
        const url = this.createUrl(environment.APPROVALS.MANAGER_PENDING_APPROVAL_LIST);

        return this.http.get<ApprovalResponse>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getApprovalsHistory(email: string): Observable<any> {
        const url = this.createUrl(environment.APPROVALS.HISTORY, email);

        return this.http.get<AppliedTimeOff[]>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    putTimeOffAction(memberEmail: string, timeOffId: string, status: string, comments: string): Observable<any> {
        let url = this.createUrl(environment.APPROVALS.ACTION, memberEmail);
        url = url.replace('{timeOffId}', timeOffId);

        const body = {
            "STATUS": status,
            "COMMENTS": comments
        };

        return this.http.put<string>(
            url,
            body
        )
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.message)
            );
    }
    
    getEvents(): Observable<any> {
        let url = this.createUrl(environment.EVENTS.LIST_EVENTS);

        return this.http.get<Events>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    getEventScores(eventId: string): Observable<any> {
        let url = this.createUrl(environment.EVENTS.GET_EVENT_SCORES);
        url = url.replace('{eventId}', eventId);

        return this.http.get<EventScores>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    addEvent(body: AddEvent): Observable<any> {
        const url = this.createUrl(environment.EVENTS.ADD_EVENT);

        return this.http.post<string>(url, body)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.message)
            );
    }

    addTeam(body: AddTeam, eventId: string): Observable<any> {
        let url = this.createUrl(environment.EVENTS.ADD_TEAM);
        url = url.replace('{eventId}', eventId);

        return this.http.post<string>(url, body)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.message)
            );
    }

    addActivity(body: AddActivity, eventId: string): Observable<any> {
        let url = this.createUrl(environment.EVENTS.ADD_ACTIVITY);
        url = url.replace('{eventId}', eventId);

        return this.http.post<string>(url, body)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.message)
            );
    }

    addScore(body: AddScore, eventId: string, teamId: string, activityId: string): Observable<any> {
        let url = this.createUrl(environment.EVENTS.ADD_SCORE);
        url = url.replace('{eventId}', eventId);
        url = url.replace('{teamId}', teamId);
        url = url.replace('{activityId}', activityId);

        return this.http.put<string>(url, body)
        .pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            map((response: any) => response.message)
        );
    }

    getHolidayList(): Observable<any> {
        const url = this.createUrl(environment.HOLIDAYS.LIST_HOLIDAYS);

        return this.http.get<any>(url)
        .pipe(
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            map((response: any) => response.body)
        );
    }

    getSalaryDetails(email: string): Observable<any> {
        const url = this.createUrl(environment.SALARY, email);

        return this.http.get<any>(url)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body)
            );
    }

    addSalary(email: string, salaryData: any): Observable<any> {
        const url = this.createUrl(environment.SALARY, email);

        return this.http.post<any>(url, salaryData)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    updateSalary(email: string, salaryData: any): Observable<any> {
        const url = this.createUrl(environment.SALARY, email);

        return this.http.put<any>(url, salaryData)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    calculateSalary(email: string, salaryData: any): Observable<any> {
        let url = this.createUrl(environment.SALARY, email);
        url = url + '/calculate';

        return this.http.post<any>(url, salaryData)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error)),
                map((response: any) => response.body || response)
            );
    }
}
